import {getSugarV1Params, libSugarUrl} from "../../utils/globals";
import {createCanvas, error, waitFor, warn} from "../../utils/utils";

/**
 * @class SugarRendererV1
 */
export default class SugarRendererV1 {
    surface = null;
    id = "";
    size = 280;
    env = "prod";
    addons = {};
    optionKeyMapping = {
        BOWL_INNER_COLOR: 'PET_BOWL_COLOR',
        CANVAS_FRAME_TYPE: 'WALL_CANVAS_FRAME_COLOR',
        WALLART_FRAME_TYPE: 'WALL_MOUNTED_FRAME_COLOR',
        WALL_CANVAS_FRAME_COLOR: 'CANVAS_FRAME_TYPE',
        WALL_MOUNTED_FRAME_COLOR: 'WALLART_FRAME_TYPE',
        FINISH: 'CASE_FINISH',
        MUG_COLOR: 'MUG_HANDLE_COLOR',
        PHOTO_MAT_COLOR_INNER: 'PHOTO_MAT_DESIGN_INNER',
        PHOTO_MAT_COLOR_OUTER: 'PHOTO_MAT_DESIGN_OUTER',
        stamp_value: 'STAMP_PRICE',
        STOCKING_COLOR: 'HOLIDAY_STOCKING_COLOR',
        PILLOW_COLOR: 'PILLOW_FABRIC',
        BACK_MATERIAL: 'PILLOW_FABRIC',
        MUG_FILLER: 'CHOCOLATE_TYPE',
        ACCENT_COLOR: 'BAG_COLOR'
    };

    /**
     * Constructing a new instance of SugarRendererV1
     * @memberOf SugarRendererV1
     * @param {String} env
     * @param {HTMLCanvasElement|String} surface - can be canvas or base64 image
     * @param {String} id
     * @param {String} size
     * @param addons {Object} addons
     * @example
     * //the example's base 64 image is 1x1 for saving space
     * const renderer = new SurfaceRenderer( "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7","<svg></svg>" )
     */
    constructor(env, surface, id, size, addons) {
        this.env = env;
        this.surface = surface;
        this.id = id;
        this.size = size;
        this.addons = this.getAddons(addons);

        this.validate();
        this.beforeRender();
    }

    getAddons(addons) {
        const newAddons = {};
        if (!addons) return newAddons;
        Object.keys(addons).forEach(addon => {
            const key = this.optionKeyMapping.hasOwnProperty(addon) ? this.optionKeyMapping[addon] : addon;
            newAddons[key] = addons[addon];
        });
        return newAddons;
    }

    /**
     * A pre-flight check before rendering for converting the surface input into base64 image for svg rendering
     * @private
     * @memberOf SugarRendererV1
     */
    beforeRender() {
        if (this.surface.constructor.name === "HTMLCanvasElement") {
            this.surface = this.surface.toDataURL();
        }
    }

    /**
     * Validates the initial input (surface and svg)
     * @private
     * @memberOf SugarRendererV1
     */
    validate() {
        if (!this.surface || (typeof this.surface === "object" && this.surface.constructor.name !== "HTMLCanvasElement")) {
            error("Validation Error", "input surface must be a canvas or base64 image");
        }

        if (!this.id) {
            error("Validation Error", "id must be an svg v1 id");
        }

        if (!this.size) {
            error("Validation Error", "size must be a number");
        }
    }

    /**
     * Renders the Sugar Product
     * @public
     * @memberOf SugarRendererV1
     * @param {Boolean} returnImage - if to return as an image (default is false, as a canvas)
     * @return HTMLCanvasElement|String
     * @example
     * renderer.render().then(canvas=>{
     *     console.log(canvas)
     * });
     */
    async render(returnImage = false) {
        try {

            try {
                await this.constructor.checkAndLoadLibSugar(this.env);
            } catch (e) {
                error("Error", `failed to load libSugar V1 - ${e.message}`);
            }

            const renderedSurface = {
                type: 'data_url',
                payload: this.surface
            }

            const collectionData = await window._sugarV1Renderer.downloadCollectionData(this.id);
            const optionsQueryString = Object.keys(this.addons).length > 0 ? new URLSearchParams(this.addons).toString() : '';

            const res = await window._sugarV1Renderer.renderDefaultInCollection(collectionData, this.size, renderedSurface, optionsQueryString);

            const tmpCanvas = res.snap.asCanvas();

            const canvas = createCanvas(tmpCanvas.width, tmpCanvas.height);

            const ctx = canvas.getContext("2d");

            ctx.drawImage(tmpCanvas, 0, 0);

            canvas.style = "";

            return returnImage ? canvas.toDataURL() : canvas;

        } catch (e) {
            error("Error", "failed to render v1");
        }
    }

    static checkAndLoadLibSugar(env) {
        return new Promise((resolve, reject) => {

            // Already loaded
            if (window._sugarV1Renderer) {
                resolve();
                return;
            }

            const initiateLibSugar = () => {

                const onReady = () => {
                    window._sugarV1Renderer.ready = true;
                    resolve();
                };

                const onFailure = (msg) => {
                    warn(`Sugar V1 Renderer:: Failure - ${msg}`)
                };

                window._sugarV1Renderer = new LibSugar(getSugarV1Params(env), onReady, onFailure);
                window._sugarV1Renderer.init();

            };


            // Load libSugar if not loaded
            if (typeof window.LibSugar !== "function") {
                if (window.loadingLibSugar) {
                    waitFor(() => (window._sugarV1Renderer?.ready), 5000, "failed to load and initiate libSugar").then(() => {
                        window.loadingLibSugar = false;
                        resolve();
                    });
                } else {
                    const script = document.createElement("script");
                    script.type = "text/javascript";

                    script.onload = () => {
                        if (window.LibSugar) {
                            initiateLibSugar();
                        } else {
                            reject();
                        }
                    };

                    script.onerror = (e) => {
                        reject(e);
                    };
                    window.loadingLibSugar = true;
                    script.src = libSugarUrl(env);

                    document.getElementsByTagName('head')[0].appendChild(script);
                }
            } else {
                initiateLibSugar();
            }
        })
    }
}
